import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";

export default function AdminStocks() {
  const [stocks, setStocks] = useState({
    kitCuisine: 0, kitSDB: 0, papierToilette: 0,
    petiteServiette: 0, grandeServiette: 0, taieOreiller: 0,
    housseCouetteSimple: 0, housseCouetteDouble: 0,
    housseMatelas90: 0, housseMatelas140: 0, housseMatelas160: 0,
    tapisBain: 0, torchon: 0,
  });
  const [editMode, setEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const updateStock = (key, value) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    setStocks(prev => ({ ...prev, [key]: numValue }));
    setHasChanges(true);
  };

  const totalKitAccueil = stocks.kitCuisine + stocks.kitSDB;
  const totalLinge = stocks.petiteServiette + stocks.grandeServiette + stocks.taieOreiller + 
    stocks.housseCouetteSimple + stocks.housseCouetteDouble + 
    stocks.housseMatelas90 + stocks.housseMatelas140 + stocks.housseMatelas160 +
    stocks.tapisBain + stocks.torchon;

  const stockCategories = [
    { title: "🧴 Kit Accueil", description: "Produits de nettoyage et hygiène", color: "violet",
      items: [{ key: "kitCuisine", label: "Kit Cuisine", sublabel: "1 par cuisine" }, { key: "kitSDB", label: "Kit Salle de bain", sublabel: "1 par SDB" }], total: totalKitAccueil },
    { title: "🧻 Papier Toilette", description: "Consommable - 1 par WC", color: "amber",
      items: [{ key: "papierToilette", label: "Rouleaux PQ", sublabel: "1 par WC/séjour" }], total: stocks.papierToilette },
    { title: "🛏️ Linge", description: "Draps, serviettes et accessoires", color: "blue",
      items: [
        { key: "petiteServiette", label: "Petites serviettes", sublabel: "" },
        { key: "grandeServiette", label: "Grandes serviettes", sublabel: "" },
        { key: "taieOreiller", label: "Taies d'oreiller", sublabel: "" },
        { key: "housseCouetteSimple", label: "Housses couette 1P", sublabel: "Simple" },
        { key: "housseCouetteDouble", label: "Housses couette 2P", sublabel: "Double" },
        { key: "housseMatelas90", label: "Housses matelas 90", sublabel: "90cm" },
        { key: "housseMatelas140", label: "Housses matelas 140", sublabel: "140cm" },
        { key: "housseMatelas160", label: "Housses matelas 160", sublabel: "160cm" },
        { key: "tapisBain", label: "Tapis de bain", sublabel: "" },
        { key: "torchon", label: "Torchons", sublabel: "" },
      ], total: totalLinge },
  ];

  const alertThresholds = {
    kitCuisine: 5, kitSDB: 5, papierToilette: 20,
    petiteServiette: 10, grandeServiette: 10, taieOreiller: 10,
    housseCouetteSimple: 5, housseCouetteDouble: 5,
    housseMatelas90: 3, housseMatelas140: 3, housseMatelas160: 3,
    tapisBain: 5, torchon: 10,
  };

  const isLow = (key) => stocks[key] <= (alertThresholds[key] || 5);

  const getColorClasses = (color) => {
    const colors = {
      violet: { bg: "bg-violet-500/10", border: "border-violet-500/20", text: "text-violet-300", badge: "bg-violet-500" },
      amber: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-300", badge: "bg-amber-500" },
      blue: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-300", badge: "bg-blue-500" },
    };
    return colors[color] || colors.violet;
  };

  return (
    <div data-testid="admin-stocks" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Gestion des stocks</h2>
          <p className="text-sm text-slate-400 mt-1">Suivi des kits accueil, papier toilette et linge</p>
        </div>
        <div className="flex gap-2">
          {editMode ? (
            <>
              <Button variant="outline" className="border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10" onClick={() => { setEditMode(false); setHasChanges(false); }}>Annuler</Button>
              <Button className="bg-emerald-500 text-white hover:bg-emerald-400" onClick={() => { toast({ title: "Stocks enregistrés", description: "Les quantités ont été mises à jour." }); setEditMode(false); setHasChanges(false); }} disabled={!hasChanges}>💾 Enregistrer</Button>
            </>
          ) : (
            <Button className="bg-violet-500 text-white hover:bg-violet-400" onClick={() => setEditMode(true)}>✏️ Modifier les stocks</Button>
          )}
        </div>
      </div>

      <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <div className="flex items-start gap-3">
          <div className="text-blue-400 text-xl">ℹ️</div>
          <div>
            <div className="text-blue-300 font-medium">Module en préparation</div>
            <div className="text-sm text-slate-400 mt-1">Ce module de gestion des stocks sera bientôt connecté aux séjours pour calculer automatiquement les besoins. Pour l&apos;instant, vous pouvez visualiser et saisir vos stocks manuellement.</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {stockCategories.map((category) => {
          const colorClasses = getColorClasses(category.color);
          return (
            <Card key={category.title} className={`${colorClasses.bg} ${colorClasses.border} border`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className={`text-lg ${colorClasses.text}`}>{category.title}</CardTitle>
                  <div className={`px-3 py-1 rounded-full ${colorClasses.badge} text-white text-sm font-bold`}>{category.total}</div>
                </div>
                <p className="text-xs text-slate-400">{category.description}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {category.items.map((item) => (
                  <div key={item.key} className={`flex items-center justify-between p-2 rounded-lg ${isLow(item.key) ? "bg-rose-500/10 border border-rose-500/20" : "bg-white/5"}`}>
                    <div>
                      <div className="text-sm text-slate-200">{item.label}</div>
                      {item.sublabel && <div className="text-xs text-slate-500">{item.sublabel}</div>}
                    </div>
                    {editMode ? (
                      <Input type="number" min="0" className="w-20 h-8 text-center border-white/20 bg-white/10" value={stocks[item.key]} onChange={(e) => updateStock(item.key, e.target.value)} />
                    ) : (
                      <div className={`text-lg font-bold ${isLow(item.key) ? "text-rose-400" : "text-white"}`}>
                        {stocks[item.key]}{isLow(item.key) && <span className="ml-1 text-xs">⚠️</span>}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-white/10 bg-white/[0.08]">
        <CardHeader><CardTitle className="text-lg text-white">📊 Résumé</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-violet-500/10 text-center"><div className="text-3xl font-bold text-violet-300">{totalKitAccueil}</div><div className="text-sm text-slate-400">Kits accueil</div></div>
            <div className="p-4 rounded-lg bg-amber-500/10 text-center"><div className="text-3xl font-bold text-amber-300">{stocks.papierToilette}</div><div className="text-sm text-slate-400">Rouleaux PQ</div></div>
            <div className="p-4 rounded-lg bg-blue-500/10 text-center"><div className="text-3xl font-bold text-blue-300">{totalLinge}</div><div className="text-sm text-slate-400">Pièces de linge</div></div>
            <div className="p-4 rounded-lg bg-rose-500/10 text-center"><div className="text-3xl font-bold text-rose-300">{Object.keys(stocks).filter(k => isLow(k)).length}</div><div className="text-sm text-slate-400">Stocks bas</div></div>
          </div>
          {Object.keys(stocks).filter(k => isLow(k)).length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
              <div className="text-rose-300 font-medium mb-2">⚠️ Alertes stock bas</div>
              <div className="flex flex-wrap gap-2">
                {Object.keys(stocks).filter(k => isLow(k)).map(key => {
                  const allItems = stockCategories.flatMap(c => c.items);
                  const item = allItems.find(i => i.key === key);
                  return (<span key={key} className="px-2 py-1 text-xs bg-rose-500/20 text-rose-300 rounded">{item?.label || key}: {stocks[key]}</span>);
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            📈 Évolution des stocks
            <span className="text-xs px-2 py-1 bg-slate-500/20 text-slate-400 rounded">Bientôt disponible</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-slate-500 border border-dashed border-white/10 rounded-lg">
            <div className="text-center">
              <div className="text-4xl mb-2">📊</div>
              <div>Graphiques d&apos;évolution à venir</div>
              <div className="text-xs mt-1">Suivi historique des entrées/sorties</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
